// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { currencySchema } from '@/currency/api/types';
import { z } from 'zod';

// Schema for validation error fields
const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

// Schema for row validation errors
const rowValidationErrorSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

// Schema for processed cost import data
const processedCostImportDataSchema = z.object({
  index: z.number(),
  amount: currencySchema.nullable(),
  description: z.string(),
  date: z.string(),
  errors: z.array(validationErrorFieldSchema).nullable(),
});

// Schema for cost upload response
const costUploadResponseSchema = z.object({
  success: z.boolean(),
  items: z.array(processedCostImportDataSchema),
  structureErrors: z.array(validationErrorFieldSchema),
  rowErrors: z.array(rowValidationErrorSchema),
});

export const uploadCostDataResponseSchema = z.object({
  data: costUploadResponseSchema,
});

export type CostDataUploadResult = z.infer<typeof costUploadResponseSchema>;

export async function uploadCostData(
  formData: FormData
): Promise<CostDataUploadResult> {
  const url = '/v1/import/cost-data';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadCostDataResponseSchema,
    response.data,
    {
      schemaName: 'UploadCostData',
      endpoint: url,
    }
  );
  return parsed.data;
}
